import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckSquare, Square, FolderSimple, X, Check, Tag, Trash, CalendarPlus, ShoppingCart } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BulkPlanDialog } from "@/components/BulkPlanDialog";
import RecipeCard from "@/components/RecipeCard";
import { MoveDialog, TagDialog, TrashDialog } from "@/components/BulkDialogs";

export function useBulkSelect() {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const stop = () => { setSelecting(false); setSelected(new Set()); };
  return { selecting, start: () => setSelecting(true), stop, selected, setSelected, toggle };
}

export function SelectModeButton({ sel, show }) {
  if (!show || sel.selecting) return null;
  return (
    <button type="button" onClick={sel.start} data-testid="bulk-select-start-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rf-ink/15 bg-white text-sm font-semibold text-rf-ink hover:bg-rf-cream transition-colors">
      <CheckSquare size={16} /> Select recipes
    </button>
  );
}

function SelectableCard({ recipe, variant, picked, onToggle }) {
  return (
    <div className="relative" data-testid={`bulk-card-${recipe.recipe_id}`}>
      <RecipeCard recipe={recipe} variant={variant} />
      <button type="button" onClick={() => onToggle(recipe.recipe_id)} aria-pressed={picked}
              data-testid={`bulk-select-${recipe.recipe_id}`} aria-label={`Select ${recipe.title}`}
              className={`absolute inset-0 z-20 rounded-[inherit] transition-colors ${picked ? "bg-rf-amber/25 ring-4 ring-rf-orange" : "bg-white/0 hover:bg-white/20"}`}>
        <span className={`absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-transform ${picked ? "bg-rf-orange text-white scale-105" : "bg-white text-rf-ink-soft"}`}>
          {picked ? <Check size={20} weight="bold" /> : <Square size={20} />}
        </span>
      </button>
    </div>
  );
}

export function BulkRecipeGrid({ recipes, sel, canSelect, variant = "default", className, testid = "bulk-recipe-grid" }) {
  return (
    <div className={`${className || ""} ${sel.selecting ? "pb-24" : ""}`} data-testid={testid}>
      {recipes.map((r) => (sel.selecting && canSelect(r)
        ? <SelectableCard key={r.recipe_id} recipe={r} variant={variant} picked={sel.selected.has(r.recipe_id)} onToggle={sel.toggle} />
        : <RecipeCard key={r.recipe_id} recipe={r} variant={variant} />))}
    </div>
  );
}

const ACTIONS = [["tags", "Tags", Tag], ["move", "Move", FolderSimple], ["plan", "Plan", CalendarPlus], ["shop", "Shop", ShoppingCart], ["trash", "Delete", Trash]];

// Local list updaters for pages after a bulk action.
export const withoutIds = (res) => (rs) => rs.filter((r) => !res.ids.includes(r.recipe_id));
export const withTags = (res) => (rs) => {
  const map = Object.fromEntries((res.updated || []).map((u) => [u.recipe_id, u.tags]));
  return rs.map((r) => (map[r.recipe_id] ? { ...r, tags: map[r.recipe_id] } : r));
};

function useCookieOffset(active) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const check = () => setOffset(document.querySelector('[data-testid="cookie-banner"]')?.offsetHeight || 0);
    check();
    const t = setInterval(check, 800);
    return () => clearInterval(t);
  }, [active]);
  return offset;
}

export function BulkMoveBar({ sel, selectable, excludeCookbookId, onMoved, onTagged, onTrashed }) {
  const [open, setOpen] = useState(null);
  const [shopping, setShopping] = useState(false);
  const nav = useNavigate();
  const cookieOffset = useCookieOffset(sel.selecting);
  if (!sel.selecting) return null;
  const n = sel.selected.size;
  const ids = [...sel.selected];
  const all = selectable.length > 0 && n === selectable.length;
  const picked = ids.map((id) => selectable.find((r) => r.recipe_id === id)).filter(Boolean);
  const selectedTags = [...new Set(picked.flatMap((r) => r.tags || []))].sort();
  const close = () => setOpen(null);
  const done = (cb, stop = true) => (res) => { close(); if (stop) sel.stop(); cb?.({ ...res, ids }); };
  const shop = async () => {
    setShopping(true);
    try {
      const { data } = await api.post("/shopping-list/add-from-recipes", { recipe_ids: ids });
      toast.success(`Added ${data.items} ingredient${data.items === 1 ? "" : "s"} from ${data.recipes} recipe${data.recipes === 1 ? "" : "s"}`,
        { description: "Matching items are combined on one list.", action: { label: "View list", onClick: () => nav("/shopping") } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add to your shopping list"); }
    setShopping(false);
  };
  return (
    <>
      {!open && <div style={cookieOffset ? { bottom: cookieOffset + 24 } : undefined} className="fixed bottom-4 inset-x-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 z-[80] rounded-2xl bg-rf-ink text-rf-cream shadow-2xl px-3 py-2.5 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 sm:min-w-[640px]" data-testid="bulk-move-bar">
        <button type="button" onClick={sel.stop} aria-label="Cancel selection" data-testid="bulk-cancel-btn" className="w-9 h-9 shrink-0 rounded-full hover:bg-white/10 flex items-center justify-center"><X size={18} /></button>
        <div className="flex sm:flex-col items-baseline sm:items-start gap-3 sm:gap-0 leading-tight min-w-0">
          <span className="text-sm font-semibold whitespace-nowrap" data-testid="bulk-selected-count">{n} selected</span>
          <button type="button" onClick={() => sel.setSelected(all ? new Set() : new Set(selectable.map((r) => r.recipe_id)))} data-testid="bulk-select-all-btn" className="text-xs font-bold underline underline-offset-2 opacity-80 hover:opacity-100 text-left">
            {all ? "Clear" : "Select all"}
          </button>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto grid grid-cols-5 sm:flex items-center gap-1">
          {ACTIONS.map(([key, label, Icon]) => (
            <button key={key} type="button" disabled={!n || (key === "shop" && shopping)} onClick={() => (key === "shop" ? shop() : setOpen(key))}
                    data-testid={key === "move" ? "bulk-move-open-btn" : key === "shop" ? "bulk-shop-btn" : `bulk-${key}-open-btn`}
                    className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-1.5 sm:px-3 py-1.5 rounded-xl text-[11px] sm:text-sm font-bold disabled:opacity-40 transition-colors ${key === "move" ? "bg-rf-amber text-rf-ink hover:brightness-105" : key === "trash" ? "text-red-300 hover:bg-white/10" : "hover:bg-white/10"}`}>
              <Icon size={18} /> {key === "shop" && shopping ? "Adding…" : label}
            </button>
          ))}
        </div>
      </div>}
      <MoveDialog open={open === "move"} onOpenChange={(o) => !o && close()} ids={ids} excludeCookbookId={excludeCookbookId} onDone={done(onMoved)} />
      <TagDialog open={open === "tags"} onOpenChange={(o) => !o && close()} ids={ids} selectedTags={selectedTags} onDone={done(onTagged, false)} />
      <TrashDialog open={open === "trash"} onOpenChange={(o) => !o && close()} ids={ids} onDone={done(onTrashed)} />
      {open === "plan" && <BulkPlanDialog open onOpenChange={(o) => !o && close()} recipes={picked} onDone={done(null)} />}
    </>
  );
}
